import { hierarchyContextReducer } from "contexts/hierarchy/hierarchyContextReducer";
import {
  ADD_DOMAIN_REPLACEMENT,
  ADD_HIERARCHY_NODES,
  ADD_SINGLE_REPLACEMENT,
  CLEAR_HIERARCHY,
  IHierarchyAction,
  LOAD_CLINICAL_LIBRARY,
  LOAD_HIERARCHY,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_DOMAIN_REPLACEMENT,
  REMOVE_HIERARCHY_COMMENT,
  REMOVE_HIERARCHY_NODE,
  REMOVE_SINGLE_REPLACEMENT,
  SAVE_HIERARCHY,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SAVE_TOC_MAJOR_VERSION,
  SET_ACCESS_RIGHTS,
  SET_CLINICAL_LIBRARY_LOADING,
  SET_HIERARCHY_COMMENT_PANEL,
  SET_HIERARCHY_ID,
  SET_HIERARCHY_NAME_SAVE_BLOCKER,
  SET_HIERARCHY_SAVE_REQUEST_STATUS,
  SET_HIERARCHY_SAVE_TOC_REQUEST_STATUS,
  SET_HIERARCHY_SUBSCRIPTION_STATUS,
  UPDATE_DOMAIN_REPLACEMENT,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
  UPDATE_SINGLE_REPLACEMENT,
  UPDATE_TLF_ITEM_INDEX,
} from "contexts/hierarchy/interface";
import {
  ADD_RECEIVED_MESSAGE,
  APPLY_ARS_UPDATES,
  CLEAR_TLF,
  CLOSE_MODAL_WINDOW,
  DISCARD_ARS_UPDATES,
  ITlfAction,
  LOAD_TLF,
  PATCH_TLF_COMMENTS,
  PROCESS_SIGNALR_NOTIFICATION,
  REDO_TLF_CHANGE,
  REMOVE_TLF_COMMENT,
  SET_DELAYED_HIERARCHY_UPDATES,
  SET_EDITOR_BLOCKS_SEARCH_PANEL,
  SET_TLF_COMMENT_PANEL,
  SET_TLF_READ_ONLY,
  SET_TLF_REQUEST_STATUS,
  SET_TLF_SUBSCRIPTION_STATUS,
  SET_VIEW_MODE,
  SHOW_MODAL_WINDOW,
  TOGGLE_TRAFFIC_LIGHTS,
  UNDO_TLF_CHANGE,
  UPDATE_TLF,
  UPDATE_TLF_COMMENT,
} from "contexts/tlf/interface";
import { tlfContextReducer } from "contexts/tlf/tlfContextReducer";
import type { ITocAction } from "contexts/toc/interface";
import { tocContextReducer } from "contexts/toc/tocContextReducer";
import { IUnifiedAction, IUnifiedState } from "./interface";

/**
 * Internal reducer state slice holding the hierarchy and ToC sub-states.
 * App-level settings are not managed by this reducer — they are derived
 * from loader data in {@link AppSettingsContextProvider}.
 */
/** Set of all operation strings that belong to the hierarchy domain */
const hierarchyOperationSet = new Set<string>([
  ADD_DOMAIN_REPLACEMENT,
  ADD_HIERARCHY_NODES,
  ADD_SINGLE_REPLACEMENT,
  CLEAR_HIERARCHY,
  LOAD_HIERARCHY,
  LOAD_CLINICAL_LIBRARY,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_DOMAIN_REPLACEMENT,
  REMOVE_HIERARCHY_COMMENT,
  REMOVE_HIERARCHY_NODE,
  REMOVE_SINGLE_REPLACEMENT,
  SAVE_HIERARCHY,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SAVE_TOC_MAJOR_VERSION,
  SET_ACCESS_RIGHTS,
  SET_CLINICAL_LIBRARY_LOADING,
  SET_HIERARCHY_COMMENT_PANEL,
  SET_HIERARCHY_ID,
  SET_HIERARCHY_NAME_SAVE_BLOCKER,
  SET_HIERARCHY_SAVE_REQUEST_STATUS,
  SET_HIERARCHY_SAVE_TOC_REQUEST_STATUS,
  SET_HIERARCHY_SUBSCRIPTION_STATUS,
  UPDATE_DOMAIN_REPLACEMENT,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
  UPDATE_SINGLE_REPLACEMENT,
  UPDATE_TLF_ITEM_INDEX,
]);

/** Set of operation strings that belong to the TLF domain. */
const tlfOperationSet = new Set<string>([
  ADD_RECEIVED_MESSAGE,
  APPLY_ARS_UPDATES,
  CLEAR_TLF,
  DISCARD_ARS_UPDATES,
  CLOSE_MODAL_WINDOW,
  LOAD_TLF,
  PATCH_TLF_COMMENTS,
  PROCESS_SIGNALR_NOTIFICATION,
  REDO_TLF_CHANGE,
  REMOVE_TLF_COMMENT,
  SET_DELAYED_HIERARCHY_UPDATES,
  SET_EDITOR_BLOCKS_SEARCH_PANEL,
  SET_TLF_REQUEST_STATUS,
  SET_TLF_REQUEST_STATUS,
  SET_TLF_COMMENT_PANEL,
  SET_TLF_READ_ONLY,
  SET_TLF_SUBSCRIPTION_STATUS,
  SET_VIEW_MODE,
  SHOW_MODAL_WINDOW,
  TOGGLE_TRAFFIC_LIGHTS,
  UNDO_TLF_CHANGE,
  UPDATE_TLF,
  UPDATE_TLF_COMMENT,
]);

/**
 * Type guard that determines whether a unified action belongs to the hierarchy domain.
 *
 * @param action - The unified action to check.
 * @returns `true` when the action is an {@link IHierarchyAction}.
 */
export const isHierarchyAction = (action: IUnifiedAction): action is IHierarchyAction =>
  hierarchyOperationSet.has(action.operation);

/**
 * Type guard that determines whether a unified action belongs to the TLF domain.
 *
 * @param action - The unified action to check.
 * @returns `true` when the action is an {@link ITlfAction}.
 */
export const isTlfAction = (action: IUnifiedAction): action is ITlfAction =>
  tlfOperationSet.has(action.operation);

/**
 * Unified reducer for the AppSettings context.
 * Delegates hierarchy domain actions to {@link hierarchyContextReducer} and
 * ToC domain actions to {@link tocContextReducer}, keeping each slice isolated.
 *
 * @param state - The current combined hierarchy, ToC, and TLF reducer state.
 * @param action - The unified action to apply.
 * @returns The updated {@link IUnifiedState}.
 */
export const appSettingsContextReducer = (
  state: IUnifiedState,
  action: IUnifiedAction,
): IUnifiedState => {
  if (isHierarchyAction(action)) {
    return { ...state, hierarchyState: hierarchyContextReducer(state.hierarchyState, action) };
  }
  if (isTlfAction(action)) {
    return { ...state, tlfState: tlfContextReducer(state.tlfState, action) };
  }
  return { ...state, tocState: tocContextReducer(state.tocState, action as ITocAction) };
};
